import "@/App.css";
import { useState, useEffect } from "react";
import {
  Star, Phone, MapPin, Clock, Menu, X,
  Stethoscope, Heart, ShieldCheck, Activity, MessageCircle, Navigation } from
"lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";

const CLINIC = {
  name: "Sri Vishnu Clinic",
  tagline: "Expert Care, Close to Home",
  rating: 5.0,
  reviewCount: 7,
  address: "Modern Complex, 49/B1, Sengathalai – Vedaranyam Road, Rastha, Vadamazhai, Vedaranyam, Tamil Nadu 614810, India",
  phone: "+91 9003512592",
  directionsUrl: "https://www.google.com/maps/dir/?api=1&destination=10.3782,79.8487&destination_place_id=Sri+Vishnu+Clinic+Vedaranyam",
  whatsappNumber: "919003512592"
};

const heroImg = "https://images.unsplash.com/photo-1659353888357-75e5858a5bc5?w=800&q=80";
const clinicImg = "https://images.unsplash.com/photo-1581982231900-6a1a46b744c9?w=800&q=80";

function Stars5() {
  return (
    <div className="flex">
      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
    </div>);

}

function StarsBig() {
  return (
    <div className="flex justify-center gap-1">
      <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
      <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
      <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
      <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
      <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
    </div>);

}

function NavBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="header-nav" className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? "bg-white/90 backdrop-blur shadow-md" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a href="#home" data-testid="logo" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-teal-700 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl text-gray-900">Sri Vishnu Clinic</span>
        </a>
        <nav data-testid="desktop-nav" className="hidden md:flex gap-8">
          <a href="#home" className="text-gray-600 hover:text-teal-700 font-medium">Home</a>
          <a href="#about" className="text-gray-600 hover:text-teal-700 font-medium">About</a>
          <a href="#services" className="text-gray-600 hover:text-teal-700 font-medium">Services</a>
          <a href="#reviews" className="text-gray-600 hover:text-teal-700 font-medium">Reviews</a>
          <a href="#contact" className="text-gray-600 hover:text-teal-700 font-medium">Contact</a>
        </nav>
        <Button data-testid="header-call-btn" className="hidden md:flex bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6">
          <Phone className="w-4 h-4 mr-2" />Call Now
        </Button>
        <button data-testid="mobile-menu-btn" className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {mobileOpen &&
      <div data-testid="mobile-menu" className="md:hidden bg-white shadow-lg p-6">
          <a href="#home" className="block py-2 text-gray-600" onClick={() => setMobileOpen(false)}>Home</a>
          <a href="#about" className="block py-2 text-gray-600" onClick={() => setMobileOpen(false)}>About</a>
          <a href="#services" className="block py-2 text-gray-600" onClick={() => setMobileOpen(false)}>Services</a>
          <a href="#reviews" className="block py-2 text-gray-600" onClick={() => setMobileOpen(false)}>Reviews</a>
          <a href="#contact" className="block py-2 text-gray-600" onClick={() => setMobileOpen(false)}>Contact</a>
        </div>
      }
    </header>);

}

function Hero() {
  return (
    <section id="home" data-testid="hero-section" className="min-h-screen pt-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div data-testid="rating-badge" className="inline-flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full mb-6">
            <Stars5 />
            <span className="font-semibold text-sm text-amber-800">{CLINIC.rating} Rating</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">{CLINIC.tagline}</h1>
          <p className="text-lg text-gray-600 mb-6 max-w-lg">
            Compassionate and quality medical care at Sri Vishnu Clinic. Experienced, kind doctor dedicated to your well-being.
          </p>
          <div className="flex flex-col gap-3 mb-8">
            <div className="flex items-center gap-3 text-gray-600">
              <MapPin className="w-5 h-5 text-teal-700" /><span>Vedaranyam, Tamil Nadu</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <Clock className="w-5 h-5 text-teal-700" /><span>Open Today: 9 AM – 8 PM</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Button data-testid="hero-call-btn" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg">
              <Phone className="w-5 h-5 mr-2" />Call Now
            </Button>
            <Button data-testid="hero-directions-btn" className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 py-6 text-lg" onClick={() => window.open(CLINIC.directionsUrl, "_blank")}>
              <Navigation className="w-5 h-5 mr-2" />Get Directions
            </Button>
          </div>
        </div>
        <div className="hidden md:block relative">
          <div className="absolute inset-0 bg-emerald-100 rounded-full blur-3xl opacity-60 -z-10" />
          <img data-testid="hero-image" src={heroImg} alt="Doctor" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/5]" />
        </div>
      </div>
    </section>);

}

function About() {
  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-emerald-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
        <img data-testid="about-image" src={clinicImg} alt="Clinic" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" />
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">About Sri Vishnu Clinic</h2>
          <p className="text-lg text-gray-600 mb-6">
            Serving Vedaranyam with dedication. Known for clean, modern environment and patient-centered healthcare.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-teal-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Experienced Doctor</h3>
                <p className="text-gray-600">Kind, knowledgeable, ethical treatment explanations.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-teal-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Patient-First Approach</h3>
                <p className="text-gray-600">Personalized attention tailored to your needs.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0">
                <Stethoscope className="w-6 h-6 text-teal-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Modern Facilities</h3>
                <p className="text-gray-600">Clean, well-equipped, comfortable environment.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
          <p className="text-lg text-gray-600">Comprehensive healthcare for you and your family</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card data-testid="service-card-0" className="bg-white rounded-xl p-6 border hover:shadow-lg transition-all">
            <CardContent className="p-0">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <Stethoscope className="w-6 h-6 text-teal-700" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">General Consultation</h3>
              <p className="text-gray-600">Comprehensive health check-ups and diagnosis with personalized treatment plans.</p>
            </CardContent>
          </Card>
          <Card data-testid="service-card-1" className="bg-white rounded-xl p-6 border hover:shadow-lg transition-all">
            <CardContent className="p-0">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-teal-700" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Chronic Disease Care</h3>
              <p className="text-gray-600">Expert management of diabetes, hypertension, and chronic conditions.</p>
            </CardContent>
          </Card>
          <Card data-testid="service-card-2" className="bg-white rounded-xl p-6 border hover:shadow-lg transition-all">
            <CardContent className="p-0">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <Activity className="w-6 h-6 text-teal-700" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Wound Care & Treatment</h3>
              <p className="text-gray-600">Specialized wound care including diabetic wounds with proven results.</p>
            </CardContent>
          </Card>
          <Card data-testid="service-card-3" className="bg-white rounded-xl p-6 border hover:shadow-lg transition-all">
            <CardContent className="p-0">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-teal-700" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Preventive Healthcare</h3>
              <p className="text-gray-600">Routine health screenings and preventive care for your family.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>);

}

function Reviews() {
  return (
    <section id="reviews" data-testid="reviews-section" className="py-20 md:py-32 bg-emerald-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Patient Reviews</h2>
          <StarsBig />
          <p className="text-gray-600 mt-2">{CLINIC.rating} stars from {CLINIC.reviewCount} Google Reviews</p>
        </div>
        <div data-testid="reviews-carousel" className="grid md:grid-cols-3 gap-6">
          <div data-testid="review-card-0" className="bg-white rounded-2xl p-6 shadow-sm">
            <Stars5 />
            <p className="text-gray-600 my-4 italic">&ldquo;One of the best clinics in Vedaranyam. The doctor is very knowledgeable and kind.&rdquo;</p>
            <div className="flex justify-between">
              <span className="!font-semibold text-gray-900">Vignesh Pandiyan
              </span>
              <span className="text-sm text-gray-400">2 months ago</span>
            </div>
          </div>
          <div data-testid="review-card-1" className="bg-white rounded-2xl p-6 shadow-sm">
            <Stars5 />
            <p className="text-gray-600 my-4 italic">&ldquo;Excellent clinic and very kind doctor. The clinic is clean and well-maintained.&rdquo;</p>
            <div className="flex justify-between">
              <span className="!font-semibold text-gray-900">Nithyashri</span>
              <span className="text-sm text-gray-400">3 months ago</span>
            </div>
          </div>
          <div data-testid="review-card-2" className="bg-white rounded-2xl p-6 shadow-sm">
            <Stars5 />
            <p className="text-gray-600 my-4 italic">&ldquo;My diabetic wound healed significantly within one month. Exceptional treatment.&rdquo;</p>
            <div className="flex justify-between">
              <span className="!font-semibold text-gray-900">Tamilarasan</span>
              <span className="text-sm text-gray-400">1 month ago</span>
            </div>
          </div>
        </div>
      </div>
    </section>);
}

function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Visit Us Today</h2>
          <p className="text-lg text-gray-600">Conveniently located in Vedaranyam for quality healthcare</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="mb-8">
              <h3 className="font-semibold text-xl text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-teal-700" />Address
              </h3>
              <p className="text-gray-600 pl-7">{CLINIC.address}</p>
            </div>
            <div className="mb-8">
              <h3 className="font-semibold text-xl text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-700" />Timings
              </h3>
              <div className="pl-7 space-y-2">
                <div className="flex justify-between max-w-sm text-gray-600">
                  <span>Monday - Saturday</span><span className="font-medium">9:00 AM – 5:00 PM</span>
                </div>
                <div className="flex justify-between max-w-sm text-gray-600">
                  <span>Sunday</span><span className="font-medium">9:00 AM – 8:00 PM</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button data-testid="contact-call-btn" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg">
                <Phone className="w-5 h-5 mr-2" />Call Now
              </Button>
              <Button data-testid="contact-whatsapp-btn" className="bg-green-500 hover:bg-green-600 text-white rounded-full px-8 py-6 text-lg" onClick={() => window.open("https://wa.me/" + CLINIC.whatsappNumber, "_blank")}>
                <MessageCircle className="w-5 h-5 mr-2" />WhatsApp
              </Button>
              <Button data-testid="contact-directions-btn" className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 py-6 text-lg" onClick={() => window.open(CLINIC.directionsUrl, "_blank")}>
                <Navigation className="w-5 h-5 mr-2" />Directions
              </Button>
            </div>
          </div>
          <div data-testid="google-map" className="rounded-2xl overflow-hidden shadow-lg h-[400px]">
            <iframe
              src="https://maps.google.com/maps?q=Sri+Vishnu+Clinic,+Vedaranyam,+Tamil+Nadu+614810&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title="Sri Vishnu Clinic Location" />

          </div>
        </div>
      </div>
    </section>);

}

function Footer() {
  return (
    <footer data-testid="footer" className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-teal-700 flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl">Sri Vishnu Clinic</span>
            </div>
            <p className="text-gray-400">Quality Medical Care in Vedaranyam</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <div className="flex flex-col gap-2 text-gray-400">
              <a href="#home" className="hover:text-white">Home</a>
              <a href="#about" className="hover:text-white">About Us</a>
              <a href="#services" className="hover:text-white">Services</a>
              <a href="#reviews" className="hover:text-white">Reviews</a>
              <a href="#contact" className="hover:text-white">Contact</a>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <div className="space-y-2 text-gray-400">
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-teal-500" />Vedaranyam, TN 614810</p>
              <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-teal-500" />{CLINIC.phone}</p>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} Sri Vishnu Clinic. All rights reserved.</p>
          <p className="mt-2">Best Medical Clinic in Vedaranyam | Quality Healthcare Tamil Nadu</p>
        </div>
      </div>
    </footer>);

}

function FloatingWhatsApp() {
  return (
    <a data-testid="floating-whatsapp" href={"https://wa.me/" + CLINIC.whatsappNumber} target="_blank" rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-green-500 flex items-center justify-center shadow-lg hover:bg-green-600 hover:scale-110 transition-all">
      <MessageCircle className="w-7 h-7 text-white" />
    </a>);

}

export default function App() {
  return (
    <div data-testid="app-container">
      <NavBar />
      <Hero />
      <About />
      <Services />
      <Reviews />
      <Contact />
      <Footer />
      <FloatingWhatsApp />
    </div>);

}